import { hubCall } from "@/lib/api/hub";
/**
 * Connector Service — calls hub proxy for connector-related endpoints
 */
import type {
  Connector,
  ConnectorSubtype,
  ConnectorShare,
  ShareTablePermission,
  CreateConnectorPayload,
  UpdateConnectorPayload,
  CreateSubtypePayload,
  UpdateSubtypePayload,
  CreateSharePayload,
  UpdateSharePayload,
  CreateShareTablePermPayload,
  UpdateShareTablePermPayload,
  ConnectorFilters,
  MyConnectorFilters,
  DatabaseInfo,
  SchemaInfo,
  TableInfo,
  ColumnInfo,
  PreviewTableDataResult,
  GoogleSheetTab,
  GoogleSheetRangeValues,
  RedisScanResult,
  RedisValuePreview,
  TestConnectorResult,
  ConnectorCounts,
  MyConnector,
} from "@/types/connector.types";
import type {
  ApiResponse,
  PaginatedResponse,
  EnrichedPaginatedResponse,
} from "@/types/api.types";




function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}

export const connectorService = {
  // =========================================================================
  // Connector CRUD
  // =========================================================================

  async list(
    filters?: ConnectorFilters
  ): Promise<EnrichedPaginatedResponse<Connector>> {
    const qs = filters ? buildQueryString(filters as Record<string, unknown>) : "";
    return hubCall<EnrichedPaginatedResponse<Connector>>(
      `/connectors${qs}`
    );
  },

  async getById(id: string): Promise<ApiResponse<Connector>> {
    return hubCall<ApiResponse<Connector>>(`/connectors/${id}`);
  },

  async create(
    payload: CreateConnectorPayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>("/connectors", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async update(
    id: string,
    payload: UpdateConnectorPayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/connectors/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  async delete(id: string): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/connectors/${id}`, {
      method: "DELETE",
    });
  },

  // =========================================================================
  // Connector Subtypes
  // =========================================================================

  async listSubtypes(
    filter?: { type?: string }
  ): Promise<PaginatedResponse<ConnectorSubtype>> {
    const qs = filter ? buildQueryString(filter as Record<string, unknown>) : "";
    return hubCall<PaginatedResponse<ConnectorSubtype>>(
      `/connector-subtypes${qs}`
    );
  },

  async getSubtype(id: string): Promise<ApiResponse<ConnectorSubtype>> {
    return hubCall<ApiResponse<ConnectorSubtype>>(
      `/connector-subtypes/${id}`
    );
  },

  async createSubtype(
    payload: CreateSubtypePayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>("/connector-subtypes", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async updateSubtype(
    id: string,
    payload: UpdateSubtypePayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/connector-subtypes/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      }
    );
  },

  async deleteSubtype(id: string): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/connector-subtypes/${id}`,
      {
        method: "DELETE",
      }
    );
  },

  // =========================================================================
  // Connector Shares
  // =========================================================================

  async listShares(
    connectorId: string,
    params?: { page?: number; limit?: number }
  ): Promise<EnrichedPaginatedResponse<ConnectorShare>> {
    const qs = params ? buildQueryString(params as Record<string, unknown>) : "";
    return hubCall<EnrichedPaginatedResponse<ConnectorShare>>(
      `/connector-shares/${connectorId}${qs}`
    );
  },

  async createShare(
    connectorId: string,
    payload: CreateSharePayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/connector-shares/${connectorId}`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    );
  },

  async updateShare(
    connectorId: string,
    shareId: string,
    payload: UpdateSharePayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/connector-shares/${connectorId}/${shareId}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      }
    );
  },

  async deleteShare(
    connectorId: string,
    shareId: string
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/connector-shares/${connectorId}/${shareId}`,
      {
        method: "DELETE",
      }
    );
  },

  // =========================================================================
  // Share Table Permissions
  // =========================================================================

  async listTablePermissions(
    shareId: string
  ): Promise<PaginatedResponse<ShareTablePermission>> {
    return hubCall<PaginatedResponse<ShareTablePermission>>(
      `/connector-share-table-permissions/${shareId}`
    );
  },

  async createTablePermission(
    shareId: string,
    payload: CreateShareTablePermPayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/connector-share-table-permissions/${shareId}`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    );
  },

  async updateTablePermission(
    shareId: string,
    permId: string,
    payload: UpdateShareTablePermPayload
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/connector-share-table-permissions/${shareId}/${permId}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      }
    );
  },

  async deleteTablePermission(
    shareId: string,
    permId: string
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(
      `/connector-share-table-permissions/${shareId}/${permId}`,
      {
        method: "DELETE",
      }
    );
  },

  // =========================================================================
  // Database Browsing
  // =========================================================================

  async getDatabases(
    connectorId: string
  ): Promise<ApiResponse<DatabaseInfo[]>> {
    return hubCall<ApiResponse<DatabaseInfo[]>>(
      `/connectors/${connectorId}/databases`
    );
  },

  async getSchemas(
    connectorId: string,
    database?: string
  ): Promise<ApiResponse<SchemaInfo[]>> {
    const qs = database ? `?database=${encodeURIComponent(database)}` : "";
    return hubCall<ApiResponse<SchemaInfo[]>>(
      `/connectors/${connectorId}/schemas${qs}`
    );
  },

  async getTables(
    connectorId: string,
    database?: string,
    schema?: string
  ): Promise<ApiResponse<TableInfo[]>> {
    const params: Record<string, unknown> = {};
    if (database) params.database = database;
    if (schema) params.schema = schema;
    const qs = buildQueryString(params);
    return hubCall<ApiResponse<TableInfo[]>>(
      `/connectors/${connectorId}/tables${qs}`
    );
  },

  async getTableColumns(
    connectorId: string,
    tableName: string,
    database?: string,
    schema?: string
  ): Promise<ApiResponse<ColumnInfo[]>> {
    const params: Record<string, unknown> = {};
    if (database) params.database = database;
    if (schema) params.schema = schema;
    const qs = buildQueryString(params);
    return hubCall<ApiResponse<ColumnInfo[]>>(
      `/connectors/${connectorId}/tables/${encodeURIComponent(tableName)}/columns${qs}`
    );
  },

  // =========================================================================
  // Table Data Preview
  // =========================================================================

  async getTableData(
    connectorId: string,
    tableName: string,
    opts?: {
      database?: string;
      schema?: string;
      limit?: number;
      offset?: number;
      sort?: string;
      sortDir?: string;
    }
  ): Promise<ApiResponse<PreviewTableDataResult>> {
    const params: Record<string, unknown> = {};
    if (opts?.limit) params.limit = opts.limit;
    if (opts?.offset) params.offset = opts.offset;
    if (opts?.database) params.database = opts.database;
    if (opts?.schema) params.schema = opts.schema;
    if (opts?.sort) params.sort = opts.sort;
    if (opts?.sortDir) params.sort_dir = opts.sortDir;
    const qs = buildQueryString(params);
    return hubCall<ApiResponse<PreviewTableDataResult>>(
      `/connectors/${connectorId}/tables/${encodeURIComponent(tableName)}/data${qs}`
    );
  },

  // =========================================================================
  // Google Sheets Browsing
  // =========================================================================

  async getGoogleSheetTabs(
    connectorId: string,
    spreadsheetId: string
  ): Promise<ApiResponse<GoogleSheetTab[]>> {
    return hubCall<ApiResponse<GoogleSheetTab[]>>(
      `/connectors/${connectorId}/sheets/spreadsheets/${encodeURIComponent(spreadsheetId)}/tabs`
    );
  },

  async getGoogleSheetValues(
    connectorId: string,
    spreadsheetId: string,
    opts: { sheet?: string; range?: string } = {}
  ): Promise<ApiResponse<GoogleSheetRangeValues>> {
    const qs = buildQueryString(opts as Record<string, unknown>);
    return hubCall<ApiResponse<GoogleSheetRangeValues>>(
      `/connectors/${connectorId}/sheets/spreadsheets/${encodeURIComponent(spreadsheetId)}/values${qs}`
    );
  },

  async startGoogleSheetsOAuth(
    connectorId: string
  ): Promise<ApiResponse<{ authUrl: string; state: string }>> {
    return hubCall<ApiResponse<{ authUrl: string; state: string }>>(
      `/connectors/google-sheets/oauth/start?connectorId=${encodeURIComponent(connectorId)}`
    );
  },

  async revokeGoogleSheetsOAuth(
    connectorId: string
  ): Promise<ApiResponse<{ tokenStatus: string }>> {
    return hubCall<ApiResponse<{ tokenStatus: string }>>(
      `/connectors/${connectorId}/google-sheets/oauth/revoke`,
      { method: "POST" }
    );
  },

  // =========================================================================
  // Redis Browsing
  // =========================================================================

  async scanRedisKeys(
    connectorId: string,
    opts: { pattern?: string; cursor?: string; count?: number } = {}
  ): Promise<ApiResponse<RedisScanResult>> {
    const qs = buildQueryString(opts as Record<string, unknown>);
    return hubCall<ApiResponse<RedisScanResult>>(`/connectors/${connectorId}/redis/keys${qs}`);
  },

  async previewRedisValue(
    connectorId: string,
    opts: { key: string; byteLimit?: number }
  ): Promise<ApiResponse<RedisValuePreview>> {
    const qs = buildQueryString(opts as Record<string, unknown>);
    return hubCall<ApiResponse<RedisValuePreview>>(`/connectors/${connectorId}/redis/value${qs}`);
  },

  // =========================================================================
  // Test Connection
  // =========================================================================

  async testConnection(
    connectorId: string
  ): Promise<ApiResponse<TestConnectorResult>> {
    return hubCall<ApiResponse<TestConnectorResult>>(
      `/connectors/${connectorId}/test`,
      { method: "POST" }
    );
  },

  async testConfig(
    subtype: string,
    config: Record<string, unknown>
  ): Promise<ApiResponse<TestConnectorResult>> {
    return hubCall<ApiResponse<TestConnectorResult>>(
      `/connectors/test-config`,
      { method: "POST", body: JSON.stringify({ subtype, config }) }
    );
  },

  // =========================================================================
  // Counts
  // =========================================================================

  async getCounts(): Promise<ApiResponse<ConnectorCounts>> {
    return hubCall<ApiResponse<ConnectorCounts>>("/connectors/counts");
  },

  // =========================================================================
  // My Connectors (permission-resolved)
  // =========================================================================

  async getMyConnectors(
    filters?: MyConnectorFilters
  ): Promise<PaginatedResponse<MyConnector>> {
    const qs = filters ? buildQueryString(filters as Record<string, unknown>) : "";
    return hubCall<PaginatedResponse<MyConnector>>(
      `/my-connectors${qs}`
    );
  },
};
